import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  CardActions,
} from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { useAuth } from '../../hooks/AuthProvider';
import SubscriptionOnboardingDialog from '../SubscriptionOnboarding/SubscriptionOnboardingDialog';
import { Button } from '../Button/Button';

interface SubscriptionCardProps {
  className?: string;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ className }) => {
  const { subscriptions } = useAuth();
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);

  const activeSubscription = subscriptions?.find((sub) => sub.isActive === true);

  return (
    <>
      <GlassmorphicCard className={`subscription-card ${className || ''}`}>
        <Box p={3}>
          {/* Header */}
          <Box display="flex" alignItems="center" mb={2}>
            <CreditCard 
              sx={{ 
                fontSize: 28, 
                background: 'linear-gradient(135deg, #f6d365, #fda085)',
                color: 'transparent',
                backgroundClip: 'text',
                mr: 1.5,
              }} 
            />
            <Typography variant="h6" fontWeight={600}>
              Subscription Details
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Subscription Content */}
          {activeSubscription ? (
            <Box className="subscription-info">
              <Typography variant="body1" mb={1}>
                <strong>Current Plan:</strong> {activeSubscription.type}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                mb={2}
                sx={{ lineHeight: 1.6 }}
              >
                {activeSubscription.description}
              </Typography>
              
              {/* Subscription benefits or features could go here */}
              <Box 
                sx={{
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: 1,
                  p: 2,
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                }}
              >
                <Typography variant="body2" color="success.main">
                  ✨ Active subscription - You have access to all premium features
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" mb={2}>
                No active subscription found.
              </Typography>
              <Box 
                sx={{
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  borderRadius: 1,
                  p: 2,
                  border: '1px solid rgba(255, 152, 0, 0.2)',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="warning.main">
                  You're not on a plan yet – pick one to unlock premium itineraries.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <CardActions sx={{ p: 3, pt: 0 }}>
          <Button
            intent="primary"
            onClick={() => setShowSubscriptionDialog(true)}
            className="manage-subscription-button"
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #f6d365, #fda085)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(246, 211, 101, 0.4)',
              },
            }}
          >
            {activeSubscription ? 'Manage Subscription' : 'Choose a Plan'}
          </Button>
        </CardActions>
      </GlassmorphicCard>

      {/* Subscription Dialog */}
      {showSubscriptionDialog && (
        <SubscriptionOnboardingDialog
          open={showSubscriptionDialog}
          onClose={() => setShowSubscriptionDialog(false)}
        />
      )}
    </>
  );
};

export default SubscriptionCard;